import React, { useCallback, useRef, useState } from "react";
import { Box, Divider, Grid, IconButton, Stack, Typography } from "@mui/material";

import CaseImage from "../CaseImage/CaseImage";
import { ImageItem } from "types/types/Types";
import CustomText from "components/common/CustomText/CustomText";
import { colors } from "styles/colors";
import useGlobalStyles from "styles/globalStyles";
import AddIcon from "icons/AddIcon";
import ImageCarousel from "components/modals/ImageCarousel/ImageCarousel";

interface Props {
  images: ImageItem[];
  setImages: (updater: any) => void;
}

export default function CaseImages({ images, setImages }: Props) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const globalStyles = useGlobalStyles();
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);

  const handleRemove = useCallback(
    (index: number) => {
      setImages((prev: ImageItem[]) => prev.filter((_, i) => i !== index));
    },
    [setImages]
  );

  const handleRename = useCallback(
    (index: number, newName: string) => {
      setImages((prev: ImageItem[]) => {
        const updated = [...prev];
        if (!updated[index]) return prev;
        updated[index] = { ...updated[index], image_name: newName };
        return updated;
      });
    },
    [setImages]
  );

  const handleAddPhotoClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileSelected = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const imageUrl = URL.createObjectURL(file);

      const newImage: ImageItem = {
        uri: imageUrl,
        image_name: `Image-${images.length + 1}`,
      } as any;

      setImages((prev: ImageItem[]) => [...prev, newImage]);

      // reset input so same file can be reselected
      e.target.value = "";
    },
    [images.length, setImages]
  );

  return (
    <>
      <Box className={globalStyles.sectionContainer}>
        {/* Header */}
        {true ? (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className={globalStyles.sectionHeader}
          >
            <Stack direction="row" alignItems="center">
              <CustomText
                text="Images"
                color={colors.primary}
                fontWeight={600}
              />
            </Stack>

            <Box mr={2}>
              <IconButton
                onClick={handleAddPhotoClick}
                className={globalStyles.sectionButton}
              >
                <AddIcon size={22} color={colors.primary} />
              </IconButton>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={handleFileSelected}
              />
            </Box>
          </Stack>
        ) : (
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Images
          </Typography>
        )}

        {images.length > 0 && <>
          <Divider />
          {/* Image List */}
          <Grid container spacing={1.5} sx={{ px: 2.5, my: 2 }}>
            {images.map((image: ImageItem, index: number) => (
              <Grid size={{ xs: 12, md: 6, lg: 4, xl: 2 }} key={`${image?.id ?? "local"}-${index}`} >
                <Box sx={{
                  mt: 1,
                  cursor: 'pointer'
                }}>
                  <CaseImage
                    index={index}
                    item={image}
                    handleRename={handleRename}
                    handleRemove={handleRemove}
                    isEditMode={true}
                    onPreview={() => {
                      setCarouselIndex(index);
                      setCarouselOpen(true);
                    }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </>}
      </Box>
      <ImageCarousel
        open={carouselOpen}
        images={images}
        initialIndex={carouselIndex}
        onClose={() => setCarouselOpen(false)}
      />

    </>
  );
}
